import './App.css'
import { Route, Routes, Navigate } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import TenantLoginPage from "./pages/TenantLoginPage";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";
import Dashboard from "./pages/Dashboard";
// import PrivateRoute from "./routes/PrivateRoute";
import { useUser, RedirectToSignIn } from "@clerk/clerk-react";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import UsersPage from "./pages/UsersPage";
import { UserProvider } from './contexts/UserContext';
// import ClientsPage from './pages/ClientsPage';
import Configurations from './pages/Configurations';
import { ClientProvider } from './contexts/ClientContext';
import InscripcionCliente from './pages/InscripcionCliente';
import ErrorBoundary from './components/common/ErrorBoundary';

import AllClientList from './components/clientes/AllClientList';
// import SepaClients from './components/sepa/SepaClients';
import SepaClientsList from './components/sepa/SepaClientsList';
import OtherPaymentList from './components/clientes/OtherPaymentList';
import DeletedClients from './components/clientes/DeletedClients';
import ProspectsList from './components/prospects/ProspectsList';
import ClientsLayout from './components/clientes/ClientsLayout';
import Reporting from './components/reports/Reporting';
import ClienteDetalle from './components/clientes/ClienteDetalle';
import CalendarioPage from './pages/CalendarioPage';
import AuditLogPage from './pages/AuditLogPage';
import ProtectedSuperAdminRoute from "./routes/ProtectedSuperAdminRoute";
import MainLayout from './components/layout/MainLayout';
import TestContractsPage from './pages/TestContractsPage';
import { SignatureTokenPage } from './pages/SignatureTokenPage';
import { AdminLayout } from './modules/admin/Layout';
import TenantList from './modules/admin/components/tenants/TenantList';
import { BrandingProvider } from './contexts/BrandingContext';
import TenantRoute from './routes/TenantRoute';
import ClientCalendarPage from './pages/ClientCalendarPage';
import ProtectedOperatorRoute from './routes/ProtectedOperatorRoute';
import ProtectedAdminRoute from './routes/ProtectedAdminRoute';
import MisClasesPage from './pages/MisClasesPage';

// Redirige a la ruta principal según el rol del usuario
const RootRedirect = () => {
  const { user } = useUser();
  const role = user?.publicMetadata?.role as string | undefined;
  const tenantCode = user?.publicMetadata?.tenantCode as string | undefined;

  if (role === 'superadmin') {
    return <Navigate to="/admin/tenants" replace />;
  }
  if (role === 'client' && tenantCode) {
    return <Navigate to={`/${tenantCode}/mi-calendario`} replace />;
  }
  // P6 fase 2 — un instructor no tiene dashboard: su única pantalla es la lista.
  if (role === 'instructor' && tenantCode) {
    return <Navigate to={`/${tenantCode}/mis-clases`} replace />;
  }
  if (tenantCode) {
    return <Navigate to={`/${tenantCode}/dashboard`} replace />;
  }
  return <Navigate to="/login" replace />;
};

function App() {
  // const { isSignedIn } = useUser();
  const { isLoaded, isSignedIn } = useUser();

  if (!isLoaded) {
    return <div className='w-screen text-center'>Cargando…</div>;
  }


  return (
    <ErrorBoundary>
    <ClientProvider>
      <>
      
      <Routes>
          <Route path="/login/*" element={<LoginPage />} />

          {/* Ruta pública para login con branding por tenant */}
          <Route path="/:tenantCode/login/*" element={<TenantLoginPage />} />

          {/* Destino de las invitaciones de Clerk: es un alta (SignUp), no un login */}
          <Route path="/:tenantCode/accept-invitation/*" element={<AcceptInvitationPage />} />

          {/* Ruta PÚBLICA para firma de contratos (sin autenticación) */}
          <Route path="/firmar/:token" element={<SignatureTokenPage />} />

          {/* Raíz: redirige según rol */}
          <Route path="/" element={isSignedIn ? <RootRedirect /> : <Navigate to="/login" replace />} />

          {/* Rutas super admin — /admin/* (estático, prioridad sobre /:tenantCode/*) */}
          <Route
            path="/admin/*"
            element={
              isSignedIn ? (
                <BrandingProvider>
                  <MainLayout>
                    <Routes>
                      <Route element={<ProtectedSuperAdminRoute />}>
                        <Route element={<AdminLayout />}>
                          <Route path="tenants" element={<TenantList />} />
                          <Route path="/" element={<Navigate to="tenants" replace />} />
                        </Route>
                      </Route>
                    </Routes>
                  </MainLayout>
                </BrandingProvider>
              ) : (
                <RedirectToSignIn />
              )
            }
          />

          {/* Rutas tenant admin — /:tenantCode/* (dinámico) */}
          <Route
            path="/:tenantCode/*"
            element={
              isSignedIn ? (
                <BrandingProvider>
                  <MainLayout>
                    <Routes>
                      <Route element={<TenantRoute />}>
                        {/* Ruta accesible por clients */}
                        <Route path="/mi-calendario" element={<ClientCalendarPage />} />

                        {/* P6 fase 2 — fuera del guard de operador: es justo lo que
                            un instructor SÍ puede ver, y lo único. */}
                        <Route path="/mis-clases" element={<MisClasesPage />} />

                        {/* Rutas protegidas — NO clients */}
                        <Route element={<ProtectedOperatorRoute />}>
                          <Route path="/" element={<Navigate to="dashboard" replace />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/inscripcion-cliente" element={<InscripcionCliente />} />

                          {/* Rutas anidadas para clientes */}
                          <Route path="/clientes" element={<ClientsLayout />}>
                            <Route index element={<AllClientList />} />
                            {/* Ruta estática antes que :clientId — sin ella "sepa"
                                caía en :clientId y ClienteDetalle intentaba cargar
                                un cliente con id "sepa" (el error que veía el PO). */}
                            {/* SEPA y Reportes exigen ADMIN en la API. Sin este
                                guard un `user` llegaba escribiendo la URL: la
                                pantalla cargaba y reventaba con 403. El menú ya
                                los escondía (`adminOnly` en ClientsSidebar). */}
                            <Route element={<ProtectedAdminRoute />}>
                              <Route path="sepa" element={<SepaClientsList />} />
                            </Route>
                            <Route path="otros-pagos" element={<OtherPaymentList />} />
                            <Route path="inactivos" element={<DeletedClients />} />
                            {/* D2 — personas de prueba. Estática y ANTES de
                                :clientId, o la URL cae en ClienteDetalle pidiendo
                                un cliente llamado "prospectos". */}
                            <Route path="prospectos" element={<ProspectsList />} />
                            {/* `Reporting` ya existía: se quedó sin enrutar al
                                sustituir la antigua ClientsPage de pestañas por
                                este layout, y App.tsx lo importaba sin usarlo.
                                Sin esta ruta, /clientes/reportes caía en
                                :clientId y ClienteDetalle pedía un cliente
                                llamado "reportes" (400 y 500 en consola). */}
                            <Route element={<ProtectedAdminRoute />}>
                              <Route path="reportes" element={<Reporting />} />
                            </Route>
                            <Route path=":clientId" element={<ClienteDetalle />} />
                          </Route>

                          <Route path="/calendario" element={<CalendarioPage />} />
                          {/* Auditoría es solo para admin: ProtectedOperatorRoute
                              únicamente aparta a los clientes, así que un rol
                              `user` entraba escribiendo la URL. */}
                          <Route element={<ProtectedAdminRoute />}>
                            <Route path="/auditoria" element={<AuditLogPage />} />
                            <Route path="/usuarios" element={<UserProvider><UsersPage /></UserProvider>} />
                            <Route path="/configuraciones" element={<Configurations />} />
                          </Route>
                          <Route path="/dev/test-contracts" element={<TestContractsPage />} />
                        </Route>
                      </Route>
                    </Routes>
                  </MainLayout>
                </BrandingProvider>
              ) : (
                <RedirectToSignIn />
              )
            }
          />
        </Routes>
        
      </>
    </ClientProvider>
    </ErrorBoundary>
  );
}

export default App
